export const thinkingModes = {
  structure: {
    id: "structure",
    title: "Navigator",
    role: "Moderator & Planer",
    color: "blue",
    tagline: "Vom Plan zur Performance.",
    description: "Du liebst Ordnung, aber ohne Karte fühlst du dich blockiert. Wir bauen dein persönliches Navigationssystem.",
    dlfStrategy: "Struktur-Schablonen & Workflow-Training",
    icon: "FiCompass",
    maps: { M: 3, A: 3, P: 4, S: 5 },
    // Dynamic week generation logic based on purpose
    getPlan: (purposeId) => {
      const plans = {
        exam: [
          { title: "Klausur-Mapping", focus: "Strukturierung des gesamten Prüfungsstoffs in 'Sichere Bank' vs. 'Wackelkandidaten'." },
          { title: "Standard-Layout", focus: "Etablierung eines sauberen Klausur-Layouts zur Vermeidung von Folgefehlern." },
          { title: "Zeit-Management", focus: "Festlegung von Zeit-Budgets pro Aufgabentyp basierend auf deiner Arbeitsgeschwindigkeit." },
          { title: "Transfer-Struktur", focus: "Entwickeln von Schemata für unbekannte Transferaufgaben (Teil C)." },
          { title: "Echtzeit-Simulation", focus: "Klausur unter Zeitdruck mit Fokus auf die Einhaltung des Navigationsplans." },
          { title: "Finales Handbuch", focus: "Dein 1-seitiger 'Schlachtplan' für den Prüfungstag." }
        ],
        university: [
          { title: "Logik-Fundament", focus: "Strukturierung formaler Aussagen und Quantoren-Notation." },
          { title: "Beweis-Gerüst", focus: "Der systematische Aufbau von direkten und indirekten Beweisen." },
          { title: "Mengenlehre-Workflow", focus: "Strukturierte Herangehensweise an abstrakte mathematische Räume." },
          { title: "Definitionssicherheit", focus: "Präzises Arbeiten mit mathematischen Definitionen als Basis für Sätze." },
          { title: "Abstraktions-Training", focus: "Strukturierung von Problemen ohne konkrete Zahlenwerte." },
          { title: "Uni-Protokoll", focus: "Dein Workflow für die erste Vorlesungswoche." }
        ],
        sprint: [
          { title: "Bestandsaufnahme", focus: "Identifikation der kritischen Lücken in den Basiskompetenzen." },
          { title: "Routine-Aufbau", focus: "Tägliche 15-Minuten-Struktur für die wichtigsten Rechenregeln." },
          { title: "Fehler-Analyse", focus: "Systematisches Protokollieren von 'Leichtsinnsfehlern' und deren Ursachen." },
          { title: "Themen-Fokus A", focus: "Intensiv-Strukturierung des aktuell schwierigsten Schulthemas." },
          { title: "Sicherheits-Check", focus: "Validierung der neuen Routinen durch gezielte Übungsblöcke." },
          { title: "Nachhaltigkeits-Plan", focus: "Etablierung deines Systems für den restlichen Term." }
        ]
      };
      return plans[purposeId] || plans.sprint;
    }
  },
  speed: {
    id: "speed",
    title: "Sprinter",
    role: "Speed-Tester & Auditor",
    color: "orange",
    tagline: "Tempo mit Präzision.",
    description: "Du bist schnell, aber Flüchtigkeitsfehler sind deine Stolpersteine. Wir rüsten dein Bremssystem auf.",
    dlfStrategy: "Fehler-Jagd & High-Speed Checks",
    icon: "FiZap",
    maps: { M: 2, A: 3, P: 5, S: 3 },
    getPlan: (purposeId) => {
      const plans = {
        exam: [
          { title: "Präzisions-Audit", focus: "Analyse deiner häufigsten Fehlerquellen unter Zeitdruck." },
          { title: "Bremspunkte", focus: "Implementierung von 30-Sekunden-Checks nach jedem Rechenschritt." },
          { title: "Fallen-Erkennung", focus: "Gezieltes Suchen nach 'Trickfragen' in alten Klausuren." },
          { title: "Korrektur-Workflow", focus: "Optimierung deiner Selbstkorrektur-Phase am Ende der Klausur." },
          { title: "High-Speed-Validation", focus: "Simulation: Maximale Punktzahl durch Präzision statt Tempo." },
          { title: "Anti-Fehler-Guide", focus: "Deine persönliche Checkliste gegen Flüchtigkeitsfehler." }
        ],
        university: [
          { title: "Notation-Check", focus: "Vermeidung von Fehlern durch schlampige mathematische Schreibweise." },
          { title: "Herleitungstempo", focus: "Schnelles, aber exaktes Nachvollziehen von Vorlesungsbeweisen." },
          { title: "Logik-Auditing", focus: "Überprüfung von Argumentationsketten auf Lücken." },
          { title: "Formaler Check", focus: "Präzision in der Anwendung von Definitionen." },
          { title: "Transfer-Speed", focus: "Schnelle Anwendung bekannter Konzepte auf neue Strukturen." },
          { title: "Exzellenz-Routine", focus: "Dein System für fehlerfreie Abgabeblätter." }
        ],
        sprint: [
          { title: "Quick-Win Basics", focus: "Fehlerfreie Beherrschung der Grundlagen (Brüche, Vorzeichen)." },
          { title: "Stopp-Schilder", focus: "Einführung von Kontrollfragen nach jeder Teilaufgabe." },
          { title: "Rechentraining", focus: "Automatisierung von Standardwegen ohne Konzentrationsverlust." },
          { title: "Qualitäts-Sprint", focus: "Bearbeitung von Aufgabenpaketen mit Fokus auf 0-Fehler-Quote." },
          { title: "Plausibilitäts-Check", focus: "Schnellschätzungen zur Verifizierung von Ergebnissen." },
          { title: "Routine-Meister", focus: "Dein Plan für dauerhaft fehlerfreies Arbeiten." }
        ]
      };
      return plans[purposeId] || plans.sprint;
    }
  },
  intuition: {
    id: "intuition",
    title: "Explorer",
    role: "Visualisierer & Ideengeber",
    color: "fuchsia",
    tagline: "Vom Gefühl zur Form.",
    description: "Du verstehst Mathe intuitiv, aber die Formelsprache ist für dich wie eine Fremdsprache.",
    dlfStrategy: "Bild-zu-Term Übersetzung",
    icon: "FiMap",
    maps: { M: 3, A: 2, P: 3, S: 2 },
    getPlan: (purposeId) => {
      const plans = {
        exam: [
          { title: "Skizzen-Katalog", focus: "Erstellung von Standard-Visualisierungen für alle Prüfungsthemen." },
          { title: "Term-Übersetzung", focus: "Training: Von der bildlichen Vorstellung zur korrekten Gleichung." },
          { title: "Begründungs-Training", focus: "Wie du deine Intuition in klausurgerechte Sätze gießt." },
          { title: "Modell-Abgleich", focus: "Prüfung: Passt mein intuitives Ergebnis zur formalen Rechnung?" },
          { title: "Klausur-Storyboarding", focus: "Aufgaben als logische Geschichte visualisieren und lösen." },
          { title: "Bild-Wörterbuch", focus: "Deine visuelle Gedächtnisstütze für die Prüfung." }
        ],
        university: [
          { title: "Geometrische Intuition", focus: "Visualisierung abstrakter Konzepte (Vektoren, Matrizen)." },
          { title: "Formalisierungs-Brücke", focus: "Schrittweise Übersetzung von Bildern in mathematische Beweise." },
          { title: "Vektorraum-Explorer", focus: "Intuitives Verständnis komplexer Dimensionen aufbauen." },
          { title: "Topologie-Skizzen", focus: "Das 'Warum' hinter der Abstraktion bildlich begreifen." },
          { title: "Theorie-Mapping", focus: "Zusammenhänge zwischen verschiedenen Modulen visualisieren." },
          { title: "Modell-Architekt", focus: "Dein System für kreative Problemlösungen an der Uni." }
        ],
        sprint: [
          { title: "Mathe-Bilder", focus: "Wiederholung der Grundlagen durch grafische Herleitungen." },
          { title: "Verständnis-Check", focus: "Erklären von Aufgaben mit eigenen Worten und Skizzen." },
          { title: "Lücken-Visualisierung", focus: "Wo genau hakt das Verständnis? Zeichnen statt Grübeln." },
          { title: "Rechen-Flow", focus: "Verbindung von intuitiven Schritten mit formalen Regeln." },
          { title: "Transfer-Erfolg", focus: "Anwendung deines 'Bauchgefühls' auf neue Aufgabentypen." },
          { title: "Explorer-Routine", focus: "Dein täglicher Weg vom Bild zur Lösung." }
        ]
      };
      return plans[purposeId] || plans.sprint;
    }
  },
  model: {
    id: "model",
    title: "Architekt",
    role: "Erklärer & Theoretiker",
    color: "emerald",
    tagline: "Tiefe mit Effizienz.",
    description: "Du willst alles beweisen, aber in der Klausur rennt dir die Zeit davon. Wir optimieren deinen Speed.",
    dlfStrategy: "Kompakt-Beweise & Zeit-Management",
    icon: "FiLayers",
    maps: { M: 5, A: 5, P: 4, S: 4 },
    getPlan: (purposeId) => {
      const plans = {
        exam: [
          { title: "Effizienz-Audit", focus: "Wo verlierst du Zeit durch zu tiefe Herleitungen?" },
          { title: "Kompakt-Notation", focus: "Standardformulierungen für schnelle, korrekte Begründungen." },
          { title: "Schema-Priorisierung", focus: "Wann reicht das Rezept? Wann braucht es die Tiefe?" },
          { title: "Zeit-Beweise", focus: "Lösen komplexer Aufgaben unter striktem Zeitlimit." },
          { title: "Minimal-Prinzip", focus: "Simulation: Maximale Punkte mit minimalem Schreibaufwand." },
          { title: "Speed-Architektur", focus: "Dein kompakter Strategieplan für die Klausur." }
        ],
        university: [
          { title: "Beweis-Eleganz", focus: "Kürzen von Beweiswegen ohne Präzisionsverlust." },
          { title: "Struktur-Transfer", focus: "Erkennung von Isomorphismen in verschiedenen Themengebieten." },
          { title: "Axiomatik-Speed", focus: "Schnellerer Umgang mit formalen Startbedingungen." },
          { title: "Theorie-Kompression", focus: "Zusammenfassung ganzer Vorlesungswochen auf Kernmodelle." },
          { title: "Forschungs-Mindset", focus: "Effizientes Erarbeiten neuer mathematischer Gebiete." },
          { title: "Master-Plan", focus: "Dein System für wissenschaftliches Arbeiten in Mathe." }
        ],
        sprint: [
          { title: "Grundlagen-Modelle", focus: "Die 'Warum'-Ebene der Basics festigen." },
          { title: "Anwendungs-Fokus", focus: "Theoretisches Wissen in praktische Rechenkraft umwandeln." },
          { title: "Erklär-Modus", focus: "Lücken schließen durch das Unterrichten anderer (Peer-Learning)." },
          { title: "Modell-Check", focus: "Validierung der eigenen mentalen Modelle gegen die Realität." },
          { title: "Transfer-Sprint", focus: "Schnelle Anwendung tiefer Konzepte auf Schulcurriculum." },
          { title: "Wissens-Netz", focus: "Dein System zur dauerhaften Vernetzung von Inhalten." }
        ]
      };
      return plans[purposeId] || plans.sprint;
    }
  },
  analysis: {
    id: "analysis",
    title: "Guardian",
    role: "Qualitätssicherung",
    color: "cyan",
    tagline: "Mut vor Perfektion.",
    description: "Dein Perfektionismus blockiert dich beim Start. Wir brechen die Barrieren auf.",
    dlfStrategy: "Micro-Starts & Resilienz-Training",
    icon: "FiShield",
    maps: { M: 4, A: 3, P: 2, S: 4 },
    getPlan: (purposeId) => {
      const plans = {
        exam: [
          { title: "Start-Ritual", focus: "Entwicklung einer Routine für die ersten 5 Minuten der Klausur." },
          { title: "Angstgegner-Check", focus: "Gezielte Micro-Aufgaben zu deinen 'Horror-Themen'." },
          { title: "Teilpunkte-Strategie", focus: "Perfektionismus ablegen: Wie man Punkte sammelt, wenn man feststeckt." },
          { title: "Resilienz-Simulation", focus: "Klausur mit bewusst eingebauten 'Schock-Aufgaben'." },
          { title: "Reset-Protokoll", focus: "Was tun bei einem Blackout? Dein Notfallplan." },
          { title: "Mut-Zertifikat", focus: "Deine Strategie für volle Performance trotz Nervosität." }
        ],
        university: [
          { title: "Abstraktions-Mut", focus: "Sich auf Probleme einlassen, die man nicht sofort durchschaut." },
          { title: "Versuchs-Kultur", focus: "Akzeptanz von Fehlversuchen in komplexen Beweisketten." },
          { title: "Peer-Support", focus: "Gemeinsames Scheitern und Lösen in Übungsgruppen." },
          { title: "Micro-Modellierung", focus: "Große Theorien in verdauliche Häppchen zerlegen." },
          { title: "Fortschritts-Log", focus: "Dokumentation von Teil-Erfolgen in der Abstraktion." },
          { title: "Akademische Resilienz", focus: "Dein Mindset für das anspruchsvolle Studium." }
        ],
        sprint: [
          { title: "Einstiegs-Hürden", focus: "Tägliches Überwinden des 'Aufschiebe-Impulses'." },
          { title: "Erfolgs-Journal", focus: "Festhalten von 3 gelösten Aufgaben pro Tag." },
          { title: "Lücken-Akzeptanz", focus: "Mutig dort anfangen, wo das Wissen fehlt." },
          { title: "Pacing", focus: "Konstante Arbeit statt 'Alles-oder-nichts'-Aktionen." },
          { title: "Stärken-Fokus", focus: "Nutzen deiner Genauigkeit als Sicherheitsanker." },
          { title: "Stetigkeits-Plan", focus: "Dein System für angstfreies Lernen im Alltag." }
        ]
      };
      return plans[purposeId] || plans.sprint;
    }
  }
};